import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import CurrencyInput from '../components/ui/CurrencyInput';
import Input from '../components/ui/Input';
import Select from '../components/ui/Select';
import { 
  DollarSign, 
  Star, 
  Settings, 
  Save, 
  RefreshCw, 
  Crown,
  Award,
  Zap,
  Info,
  Plus,
  Gift,
  Edit,
  Trash2,
  X,
  Users,
  UserPlus,
  Lock,
  Copy,
  Check,
  LayoutGrid,
  Ban,
  PackageCheck,
  Clock
} from 'lucide-react';

interface ClubConfig {
  peso_rate: number;
  points_per_1000_pesos: number;
  updated_at: string;
}

interface MembershipLevel {
  id: string;
  level_name: string;
  min_points: number;
  max_points: number | null;
  color_primary: string;
  color_secondary: string;
  benefits: string[];
  is_active: boolean;
}

interface RedemptionItem {
  id: string;
  name: string;
  description: string;
  points_cost: number;
  category: string;
  is_active: boolean;
  max_redemptions_per_client?: number;
  total_available?: number;
  created_at: string;
  updated_at: string;
}

const ClubProductexConfig: React.FC = () => {
  const { user, userRole } = useAuth();
  const [config, setConfig] = useState<ClubConfig | null>(null);
  const [levels, setLevels] = useState<MembershipLevel[]>([]);
  const [redemptions, setRedemptions] = useState<RedemptionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newPesoRate, setNewPesoRate] = useState('1');
  const [editingLevel, setEditingLevel] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Partial<MembershipLevel>>({});
  const [showAddRedemption, setShowAddRedemption] = useState(false);
  const [editingRedemption, setEditingRedemption] = useState<string | null>(null);
  const [redemptionForm, setRedemptionForm] = useState<Partial<RedemptionItem>>({});
  const [showAddLevel, setShowAddLevel] = useState(false);
  const [newLevelForm, setNewLevelForm] = useState<Partial<MembershipLevel>>({ level_name: '', min_points: 0, max_points: null, color_primary: '#3B82F6', color_secondary: '#FFFFFF', benefits: [], is_active: true });
  const [clubClients, setClubClients] = useState<any[]>([]);
  const [adjustClientId, setAdjustClientId] = useState<string | null>(null);
  const [adjustAmount, setAdjustAmount] = useState('');
  const [adjustReason, setAdjustReason] = useState('');
  const [showPortalModal, setShowPortalModal] = useState(false);
  const [portalClientId, setPortalClientId] = useState('');
  const [portalEmail, setPortalEmail] = useState('');
  const [portalNombre, setPortalNombre] = useState('');
  const [portalEmpresa, setPortalEmpresa] = useState('');
  const [tempPassword, setTempPassword] = useState('');
  const [copied, setCopied] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordClientId, setPasswordClientId] = useState('');
  const [passwordClientName, setPasswordClientName] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [redemptionRequests, setRedemptionRequests] = useState<any[]>([]);

  useEffect(() => {
    if (userRole === 'Administrador') {
      loadConfig();
      loadLevels();
      loadRedemptions();
      loadClubClients();
      loadRedemptionRequests();
    }
  }, [userRole]);

  const loadRedemptionRequests = async () => {
    try {
      const { data, error } = await supabase.rpc('admin_list_redemption_requests');
      if (error) throw error;
      setRedemptionRequests(data || []);
    } catch (error) {
      console.error('Error loading redemption requests:', error);
    }
  };

  const updateRedemptionStatus = async (id: string, newStatus: string) => {
    setSaving(true);
    try {
      const { data, error } = await supabase.rpc('admin_update_redemption_status', {
        p_redemption_id: id,
        p_new_status: newStatus
      });
      if (error) throw error;
      const res = typeof data === 'object' ? data : (data ? JSON.parse(data) : null);
      if (res && res.success === false) throw new Error(res.error);
      await loadRedemptionRequests();
    } catch (error) {
      console.error('Error updating status:', error);
      alert(error instanceof Error ? error.message : 'Error al actualizar');
    } finally {
      setSaving(false);
    }
  };

  const loadConfig = async () => {
    try {
      const { data, error } = await supabase
        .from('club_productex_config')
        .select('*')
        .order('updated_at', { ascending: false })
        .limit(1);

      if (error) throw error;
      if (data && data.length > 0) {
        setConfig(data[0]);
        setNewPesoRate('1');
      }
    } catch (error) {
      console.error('Error loading config:', error);
    }
  };

  const loadLevels = async () => {
    try {
      const { data, error } = await supabase
        .from('club_productex_levels')
        .select('*')
        .order('min_points', { ascending: true });

      if (error) throw error;
      setLevels(data || []);
    } catch (error) {
      console.error('Error loading levels:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadRedemptions = async () => {
    try {
      const { data, error } = await supabase
        .from('club_productex_redemptions')
        .select('*')
        .order('points_cost', { ascending: true });
      if (error) throw error;
      setRedemptions(data || []);
    } catch (error) {
      console.error('Error loading redemptions:', error);
    }
  };

  const loadClubClients = async () => {
    try {
      const { data, error } = await supabase.rpc('admin_list_club_clients');
      if (error) throw error;
      setClubClients(data || []);
    } catch (error) {
      console.error('Error loading club clients:', error);
    }
  };

  const handleAdjustPoints = async () => {
    if (!adjustClientId || !adjustAmount) return;
    const amt = parseInt(adjustAmount);
    if (isNaN(amt) || amt === 0) {
      alert('Ingresa un monto válido (positivo para sumar, negativo para restar)');
      return;
    }
    setSaving(true);
    try {
      const { data, error } = await supabase.rpc('admin_adjust_client_points', {
        p_client_id: adjustClientId,
        p_amount: amt,
        p_reason: adjustReason || null
      });
      if (error) throw error;
      const res = typeof data === 'object' ? data : JSON.parse(data || '{}');
      if (res.success === false) throw new Error(res.error);
      alert('Ajuste aplicado correctamente');
      setAdjustClientId(null);
      setAdjustAmount('');
      setAdjustReason('');
      loadClubClients();
    } catch (error) {
      alert(`Error: ${error instanceof Error ? error.message : 'Error al ajustar'}`);
    } finally {
      setSaving(false);
    }
  };

  const openPortalModal = (client: any) => {
    setPortalClientId(client.client_id);
    setPortalEmail(client.client_email || '');
    setPortalNombre(client.client_name || '');
    setPortalEmpresa(client.client_company || '');
    setTempPassword('');
    setCopied(false);
    setShowPortalModal(true);
  };

  const handleCreatePortal = async () => {
    if (!portalClientId || !portalEmail) {
      alert('Email es obligatorio');
      return;
    }
    setSaving(true);
    try {
      const { data, error } = await supabase.rpc('admin_create_portal_account_for_client', {
        p_internal_client_id: portalClientId,
        p_email: portalEmail,
        p_nombre: portalNombre || null,
        p_empresa: portalEmpresa || null
      });
      if (error) throw error;
      const row = Array.isArray(data) ? data?.[0] : data;
      if (row?.temp_password) setTempPassword(row.temp_password);
      loadClubClients();
    } catch (e: any) {
      alert(e.message || 'Error al crear cuenta');
    } finally {
      setSaving(false);
    }
  };

  const openPasswordModal = (client: any) => {
    setPasswordClientId(client.portal_cliente_id);
    setPasswordClientName(client.client_name || '');
    setNewPassword('');
    setShowPasswordModal(true);
  };

  const handleChangePassword = async () => {
    if (!passwordClientId || newPassword.length < 6) {
      alert('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    setSaving(true);
    try {
      const { error } = await supabase.rpc('admin_change_client_portal_password', {
        p_portal_cliente_id: passwordClientId,
        p_new_password: newPassword
      });
      if (error) throw error;
      alert('Contraseña actualizada');
      setShowPasswordModal(false);
    } catch (e: any) {
      alert(e.message || 'Error al cambiar contraseña');
    } finally {
      setSaving(false);
    }
  };

  const updatePesoRate = async () => {
    setSaving(true);
    try {
      const { data, error } = await supabase.rpc('admin_update_peso_rate', {
        p_new_rate: 1
      });

      if (error) throw error;

      await loadConfig();
      alert('Configuración actualizada correctamente');
    } catch (error) {
      console.error('Error updating peso rate:', error);
      alert('Error al actualizar la configuración');
    } finally {
      setSaving(false);
    }
  };

  const updateMembershipLevel = async (levelId: string) => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from('club_productex_levels')
        .update({
          min_points: editForm.min_points ?? 0,
          max_points: editForm.max_points ?? null,
          color_primary: editForm.color_primary || '#3B82F6',
          color_secondary: editForm.color_secondary || '#FFFFFF',
          benefits: editForm.benefits || [],
          is_active: editForm.is_active ?? true,
          updated_at: new Date().toISOString()
        })
        .eq('id', levelId);
      if (error) throw error;
      await loadLevels();
      setEditingLevel(null);
      setEditForm({});
      alert('Nivel actualizado correctamente');
    } catch (error) {
      console.error('Error updating level:', error);
      alert('Error al actualizar el nivel');
    } finally {
      setSaving(false);
    }
  };

  const addLevel = async () => {
    if (!newLevelForm.level_name || newLevelForm.min_points == null) {
      alert('Completa nombre y puntos mínimos');
      return;
    }
    setSaving(true);
    try {
      const { error } = await supabase.from('club_productex_levels').insert({
        level_name: newLevelForm.level_name,
        min_points: newLevelForm.min_points,
        max_points: newLevelForm.max_points ?? null,
        color_primary: newLevelForm.color_primary || '#3B82F6',
        color_secondary: newLevelForm.color_secondary || '#FFFFFF',
        benefits: newLevelForm.benefits || [],
        is_active: newLevelForm.is_active ?? true
      });
      if (error) throw error;
      await loadLevels();
      setShowAddLevel(false);
      setNewLevelForm({ level_name: '', min_points: 0, max_points: null, color_primary: '#3B82F6', color_secondary: '#FFFFFF', benefits: [], is_active: true });
      alert('Nivel agregado correctamente');
    } catch (error) {
      console.error('Error adding level:', error);
      alert('Error al agregar el nivel');
    } finally {
      setSaving(false);
    }
  };

  const deleteLevel = async (id: string, name: string) => {
    if (!confirm(`¿Eliminar nivel "${name}"?`)) return;
    setSaving(true);
    try {
      const { error } = await supabase.from('club_productex_levels').delete().eq('id', id);
      if (error) throw error;
      await loadLevels();
      setEditingLevel(null);
      alert('Nivel eliminado');
    } catch (error) {
      console.error('Error deleting level:', error);
      alert('Error al eliminar');
    } finally {
      setSaving(false);
    }
  };

  const startEditing = (level: MembershipLevel) => {
    setEditingLevel(level.id);
    setEditForm(level);
  };

  const cancelEditing = () => {
    setEditingLevel(null);
    setEditForm({});
  };

  const saveRedemption = async () => {
    if (!redemptionForm.name || !redemptionForm.points_cost || !redemptionForm.category) {
      alert('Por favor completa todos los campos obligatorios');
      return;
    }

    setSaving(true);
    try {
      if (editingRedemption) {
        // Update existing redemption
        const { error } = await supabase
          .from('club_productex_redemptions')
          .update({
            name: redemptionForm.name,
            description: redemptionForm.description || '',
            points_cost: redemptionForm.points_cost,
            category: redemptionForm.category,
            is_active: redemptionForm.is_active ?? true,
            max_redemptions_per_client: redemptionForm.max_redemptions_per_client || null,
            total_available: redemptionForm.total_available || null,
            updated_at: new Date().toISOString()
          })
          .eq('id', editingRedemption);

        if (error) throw error;
        alert('Producto actualizado correctamente');
      } else {
        // Create new redemption
        const { error } = await supabase
          .from('club_productex_redemptions')
          .insert({
            name: redemptionForm.name,
            description: redemptionForm.description || '',
            points_cost: redemptionForm.points_cost,
            category: redemptionForm.category,
            is_active: redemptionForm.is_active ?? true,
            max_redemptions_per_client: redemptionForm.max_redemptions_per_client || null,
            total_available: redemptionForm.total_available || null
          });

        if (error) throw error;
        alert('Producto agregado correctamente');
      }

      await loadRedemptions();
      setShowAddRedemption(false);
      setEditingRedemption(null);
      setRedemptionForm({});
    } catch (error) {
      console.error('Error saving redemption:', error);
      alert('Error al guardar el producto');
    } finally {
      setSaving(false);
    }
  };

  const deleteRedemption = async (id: string) => {
    if (!confirm('¿Estás seguro de que deseas eliminar este producto?')) return;

    setSaving(true);
    try {
      const { error } = await supabase
        .from('club_productex_redemptions')
        .delete()
        .eq('id', id);

      if (error) throw error;
      alert('Producto eliminado correctamente');
      await loadRedemptions();
    } catch (error) {
      console.error('Error deleting redemption:', error);
      const code = (error as { code?: string } | null)?.code;
      if (code === '23503') {
        // Si ya hubo canjes asociados, no se puede borrar por FK.
        // En ese caso lo "eliminamos" del catálogo desactivándolo.
        const { error: deactivateError } = await supabase
          .from('club_productex_redemptions')
          .update({ is_active: false })
          .eq('id', id);

        if (deactivateError) {
          console.error('Error deactivating redemption:', deactivateError);
          alert('No se pudo eliminar ni desactivar el producto');
          return;
        }

        alert('Este producto ya tiene canjes. Se desactivó para que no aparezca en el catálogo.');
        await loadRedemptions();
        return;
      }

      alert('Error al eliminar el producto');
    } finally {
      setSaving(false);
    }
  };

  const startEditingRedemption = (redemption: RedemptionItem) => {
    setEditingRedemption(redemption.id);
    setRedemptionForm(redemption);
  };

  const cancelEditingRedemption = () => {
    setEditingRedemption(null);
    setRedemptionForm({});
  };

  const startAddingRedemption = () => {
    setShowAddRedemption(true);
    setRedemptionForm({
      name: '',
      description: '',
      points_cost: 0,
      category: '',
      is_active: true,
      max_redemptions_per_client: null,
      total_available: null
    });
  };

  const cancelAddingRedemption = () => {
    setShowAddRedemption(false);
    setRedemptionForm({});
  };

  const getLevelIcon = (levelName: string) => {
    switch (levelName.toLowerCase()) {
      case 'starter': return <Zap className="w-5 h-5" />;
      case 'gold': return <Star className="w-5 h-5" />;
      case 'silver': return <Award className="w-5 h-5" />;
      case 'black': return <Crown className="w-5 h-5" />;
      default: return <Star className="w-5 h-5" />;
    }
  };

  if (userRole !== 'Administrador') {
    return (
      <div className="p-6">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600">Acceso Denegado</h1>
          <p className="text-gray-600">Solo los administradores pueden acceder a esta página.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
          <span className="ml-2">Cargando configuración...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center space-x-3">
        <Crown className="w-8 h-8 text-yellow-600" />
        <h1 className="text-3xl font-bold text-gray-900">Club Productex® - Configuración</h1>
      </div>

      {/* Peso Configuration */}
      <Card className="p-6">
        <div className="flex items-center space-x-3 mb-4">
          <DollarSign className="w-6 h-6 text-green-600" />
          <h2 className="text-xl font-semibold">Sistema de Puntos</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="p-6 bg-gradient-to-br from-green-50 to-green-100 rounded-lg border-2 border-green-200">
            <div className="text-center">
              <p className="text-sm font-medium text-gray-700 mb-2">Conversión Actual</p>
              <div className="text-4xl font-bold text-green-700 mb-2">
                $1,000 = 1<Star className="inline w-8 h-8 ml-2 text-yellow-500" />
              </div>
              <p className="text-xs text-gray-600">
                Cada $1,000 pesos argentinos equivale a 1 punto
              </p>
            </div>
          </div>

          <div className="p-6 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg border-2 border-blue-200">
            <div className="text-center">
              <p className="text-sm font-medium text-gray-700 mb-2">Última Actualización</p>
              <div className="text-2xl font-bold text-blue-700 mb-2">
                {config?.updated_at ? new Date(config.updated_at).toLocaleDateString('es-AR') : 'N/A'}
              </div>
              <p className="text-xs text-gray-600">
                Sistema: {config?.points_per_1000_pesos || 1} punto por cada $1,000
              </p>
            </div>
          </div>
        </div>

        <div className="mt-6 p-4 bg-blue-50 rounded-lg">
          <p className="text-sm text-blue-800"><strong>Cómo funcionan los puntos:</strong></p>
          <ul className="list-disc list-inside text-sm text-blue-700 mt-2 space-y-1">
            <li>Cada $1,000 pesos en compras = 1 punto Club Productex</li>
            <li>Los puntos se acumulan automáticamente con cada factura</li>
            <li>Las notas de crédito restan puntos</li>
            <li>Los puntos pueden canjearse por productos y beneficios exclusivos</li>
            <li><strong>El nivel de membresía</strong> se calcula según el <strong>acumulado total de los últimos 12 meses</strong> (ventana móvil). Los canjes <strong>no reducen tu nivel</strong>.</li>
          </ul>
        </div>
      </Card>

      {/* Membership Levels Configuration */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <Settings className="w-6 h-6 text-purple-600" />
            <h2 className="text-xl font-semibold">Niveles de Membresía</h2>
          </div>
          <Button onClick={() => setShowAddLevel(true)} icon={<Plus className="w-4 h-4" />}>
            Agregar Nivel
          </Button>
        </div>

        {showAddLevel && (
          <div className="mb-6 p-4 border rounded-lg bg-gray-50">
            <h3 className="font-medium mb-3">Nuevo nivel</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
                <Input value={newLevelForm.level_name || ''} onChange={(e) => setNewLevelForm({...newLevelForm, level_name: e.target.value})} placeholder="Ej: Platinum" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Puntos mín.</label>
                <Input type="number" value={newLevelForm.min_points ?? ''} onChange={(e) => setNewLevelForm({...newLevelForm, min_points: parseInt(e.target.value) || 0})} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Puntos máx. (vacío = ∞)</label>
                <Input type="number" value={newLevelForm.max_points ?? ''} onChange={(e) => setNewLevelForm({...newLevelForm, max_points: e.target.value ? parseInt(e.target.value) : null})} placeholder="∞" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Color primario</label>
                <Input value={newLevelForm.color_primary || ''} onChange={(e) => setNewLevelForm({...newLevelForm, color_primary: e.target.value})} placeholder="#3B82F6" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Beneficios (separados por coma)</label>
                <Input value={newLevelForm.benefits?.join(', ') || ''} onChange={(e) => setNewLevelForm({...newLevelForm, benefits: e.target.value.split(',').map(b => b.trim())})} />
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <Button onClick={addLevel} disabled={saving}>Guardar</Button>
              <Button variant="outline" onClick={() => { setShowAddLevel(false); setNewLevelForm({ level_name: '', min_points: 0, max_points: null, color_primary: '#3B82F6', color_secondary: '#FFFFFF', benefits: [], is_active: true }); }}>Cancelar</Button>
            </div>
          </div>
        )}
        
        <div className="space-y-4">
          {levels.map((level) => (
            <div key={level.id} className="border rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div 
                    className="w-10 h-10 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: level.color_primary, color: level.color_secondary }}
                  >
                    {getLevelIcon(level.level_name)}
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">{level.level_name}</h3>
                    <p className="text-sm text-gray-600">
                      {level.min_points.toLocaleString()} - {level.max_points ? level.max_points.toLocaleString() : '∞'} puntos
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    level.is_active 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {level.is_active ? 'Activo' : 'Inactivo'}
                  </span>
                  
                  {editingLevel === level.id ? (
                    <div className="flex space-x-2">
                      <Button size="sm" onClick={() => updateMembershipLevel(level.id)} disabled={saving}>
                        {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                      </Button>
                      <Button size="sm" variant="outline" onClick={cancelEditing}>Cancelar</Button>
                    </div>
                  ) : (
                    <div className="flex space-x-2">
                      <Button size="sm" variant="outline" onClick={() => startEditing(level)}>Editar</Button>
                      <Button size="sm" variant="outline" onClick={() => deleteLevel(level.id, level.level_name)} className="text-red-600">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
              
              {editingLevel === level.id ? (
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Puntos Mínimos
                    </label>
                    <input
                      type="number"
                      value={editForm.min_points || ''}
                      onChange={(e) => setEditForm({...editForm, min_points: parseInt(e.target.value)})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Puntos Máximos
                    </label>
                    <input
                      type="number"
                      value={editForm.max_points || ''}
                      onChange={(e) => setEditForm({...editForm, max_points: e.target.value ? parseInt(e.target.value) : null})}
                      placeholder="∞"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Color Primario (HEX)
                    </label>
                    <input
                      type="text"
                      value={editForm.color_primary || ''}
                      onChange={(e) => setEditForm({...editForm, color_primary: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Color Secundario (HEX)
                    </label>
                    <input
                      type="text"
                      value={editForm.color_secondary || ''}
                      onChange={(e) => setEditForm({...editForm, color_secondary: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Beneficios (separados por comas)
                    </label>
                    <input
                      type="text"
                      value={editForm.benefits?.join(', ') || ''}
                      onChange={(e) => setEditForm({...editForm, benefits: e.target.value.split(',').map(b => b.trim())})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  
                  <div className="md:col-span-2">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={editForm.is_active || false}
                        onChange={(e) => setEditForm({...editForm, is_active: e.target.checked})}
                        className="mr-2"
                      />
                      <span className="text-sm font-medium text-gray-700">Nivel Activo</span>
                    </label>
                  </div>
                </div>
              ) : (
                <div className="mt-3">
                  <div className="flex flex-wrap gap-2">
                    {level.benefits?.map((benefit, index) => (
                      <span
                        key={index}
                        className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full"
                      >
                        {benefit}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </Card>

      {/* Redemption Products Management */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <Gift className="w-6 h-6 text-red-600" />
            <h2 className="text-xl font-semibold">Productos de Canje</h2>
          </div>
          <Button
            onClick={startAddingRedemption}
            icon={<Plus className="w-4 h-4" />}
          >
            Agregar Producto
          </Button>
        </div>

        {/* Add/Edit Redemption Form */}
        {(showAddRedemption || editingRedemption) && (
          <div className="mb-6 p-4 border rounded-lg bg-gray-50">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre del Producto *
                </label>
                <Input
                  value={redemptionForm.name || ''}
                  onChange={(e) => setRedemptionForm({...redemptionForm, name: e.target.value})}
                  placeholder="Ej: Descuento 10%"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Categoría *
                </label>
                <Select
                  value={redemptionForm.category || ''}
                  onChange={(value) => setRedemptionForm({...redemptionForm, category: value})}
                  options={[
                    { value: '', label: 'Seleccionar categoría' },
                    { value: 'descuentos', label: 'Descuentos' },
                    { value: 'productos', label: 'Productos' },
                    { value: 'servicios', label: 'Servicios' },
                    { value: 'experiencias', label: 'Experiencias' },
                    { value: 'otros', label: 'Otros' }
                  ]}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Costo en Puntos *
                </label>
                <Input
                  type="number"
                  value={redemptionForm.points_cost || ''}
                  onChange={(e) => setRedemptionForm({...redemptionForm, points_cost: parseInt(e.target.value) || 0})}
                  placeholder="1000"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Máximo por Cliente
                </label>
                <Input
                  type="number"
                  value={redemptionForm.max_redemptions_per_client || ''}
                  onChange={(e) => setRedemptionForm({...redemptionForm, max_redemptions_per_client: e.target.value ? parseInt(e.target.value) : null})}
                  placeholder="Sin límite"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Total Disponible
                </label>
                <Input
                  type="number"
                  value={redemptionForm.total_available || ''}
                  onChange={(e) => setRedemptionForm({...redemptionForm, total_available: e.target.value ? parseInt(e.target.value) : null})}
                  placeholder="Sin límite"
                />
              </div>
              
              <div className="flex items-center">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={redemptionForm.is_active ?? true}
                    onChange={(e) => setRedemptionForm({...redemptionForm, is_active: e.target.checked})}
                    className="mr-2"
                  />
                  <span className="text-sm font-medium text-gray-700">Producto Activo</span>
                </label>
              </div>
              
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descripción
                </label>
                <textarea
                  value={redemptionForm.description || ''}
                  onChange={(e) => setRedemptionForm({...redemptionForm, description: e.target.value})}
                  placeholder="Descripción detallada del producto o servicio..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                />
              </div>
            </div>
            
            <div className="flex justify-end space-x-2 mt-4">
              <Button
                onClick={editingRedemption ? cancelEditingRedemption : cancelAddingRedemption}
                variant="outline"
                icon={<X className="w-4 h-4" />}
              >
                Cancelar
              </Button>
              <Button
                onClick={saveRedemption}
                disabled={saving}
                icon={saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              >
                {editingRedemption ? 'Actualizar' : 'Agregar'} Producto
              </Button>
            </div>
          </div>
        )}

        {/* Redemption Products List */}
        <div className="space-y-4">
          {redemptions.map((redemption) => (
            <div key={redemption.id} className="border rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="font-semibold text-lg text-gray-900">{redemption.name}</h3>
                    <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full capitalize">
                      {redemption.category}
                    </span>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      redemption.is_active 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {redemption.is_active ? 'Activo' : 'Inactivo'}
                    </span>
                  </div>
                  
                  {redemption.description && (
                    <p className="text-gray-600 text-sm mb-2">{redemption.description}</p>
                  )}
                  
                  <div className="flex items-center space-x-4 text-sm text-gray-500">
                    <span className="font-medium text-blue-600">
                      {redemption.points_cost.toLocaleString()} puntos
                    </span>
                    {redemption.max_redemptions_per_client && (
                      <span>Máx. {redemption.max_redemptions_per_client} por cliente</span>
                    )}
                    {redemption.total_available && (
                      <span>{redemption.total_available} disponibles</span>
                    )}
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  <Button
                    size="sm"
                    variant="outline"
                    icon={<Edit className="w-4 h-4" />}
                    onClick={() => startEditingRedemption(redemption)}
                  >
                    Editar
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    icon={<Trash2 className="w-4 h-4" />}
                    onClick={() => deleteRedemption(redemption.id)}
                    className="text-red-600 hover:text-red-700"
                  >
                    Eliminar
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {redemptions.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <Gift className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p>No hay productos de canje configurados.</p>
            <p className="text-sm">Haz clic en "Agregar Producto" para comenzar.</p>
          </div>
        )}
      </Card>

      {/* Kanban Pedidos de Canje */}
      <Card className="p-6">
        <div className="flex items-center space-x-3 mb-4">
          <LayoutGrid className="w-6 h-6 text-indigo-600" />
          <h2 className="text-xl font-semibold">Pedidos de Canje</h2>
        </div>
        <p className="text-sm text-gray-600 mb-4">Gestiona los canjes solicitados por los clientes. Arrastra o cambia el estado.</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="rounded-lg border-2 border-amber-200 bg-amber-50/50 p-4 min-h-[200px]">
            <h3 className="font-semibold text-amber-800 mb-3 flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Pendiente
            </h3>
            <div className="space-y-2">
              {redemptionRequests.filter((r: any) => r.status === 'pending' || r.status === 'processing').map((r: any) => (
                <div key={r.id} className="p-3 bg-white rounded-lg border border-amber-100 shadow-sm">
                  <p className="font-medium text-gray-900 text-sm">{r.client_name}</p>
                  <p className="text-xs text-gray-600">{r.redemption_name} · {r.points_spent} pts</p>
                  <p className="text-xs text-gray-500 mt-1">{new Date(r.created_at).toLocaleDateString('es-AR')}</p>
                  <div className="flex gap-1 mt-2">
                    <button onClick={() => updateRedemptionStatus(r.id, 'completed')} className="text-xs px-2 py-1 rounded bg-green-100 text-green-800 hover:bg-green-200" disabled={saving}>Entregado</button>
                    <button onClick={() => updateRedemptionStatus(r.id, 'rejected')} className="text-xs px-2 py-1 rounded bg-red-100 text-red-800 hover:bg-red-200" disabled={saving}>Rechazar</button>
                  </div>
                </div>
              ))}
              {redemptionRequests.filter((r: any) => r.status === 'pending' || r.status === 'processing').length === 0 && (
                <p className="text-sm text-gray-500">Sin pendientes</p>
              )}
            </div>
          </div>
          <div className="rounded-lg border-2 border-red-200 bg-red-50/50 p-4 min-h-[200px]">
            <h3 className="font-semibold text-red-800 mb-3 flex items-center gap-2">
              <Ban className="w-4 h-4" />
              Rechazado
            </h3>
            <div className="space-y-2">
              {redemptionRequests.filter((r: any) => r.status === 'rejected' || r.status === 'cancelled').map((r: any) => (
                <div key={r.id} className="p-3 bg-white rounded-lg border border-red-100 shadow-sm">
                  <p className="font-medium text-gray-900 text-sm">{r.client_name}</p>
                  <p className="text-xs text-gray-600">{r.redemption_name} · {r.points_spent} pts</p>
                  <p className="text-xs text-gray-500">{new Date(r.created_at).toLocaleDateString('es-AR')}</p>
                  <button onClick={() => updateRedemptionStatus(r.id, 'pending')} className="text-xs mt-2 px-2 py-1 rounded bg-gray-100 text-gray-700 hover:bg-gray-200" disabled={saving}>Mover a Pendiente</button>
                </div>
              ))}
              {redemptionRequests.filter((r: any) => r.status === 'rejected' || r.status === 'cancelled').length === 0 && (
                <p className="text-sm text-gray-500">Ninguno</p>
              )}
            </div>
          </div>
          <div className="rounded-lg border-2 border-green-200 bg-green-50/50 p-4 min-h-[200px]">
            <h3 className="font-semibold text-green-800 mb-3 flex items-center gap-2">
              <PackageCheck className="w-4 h-4" />
              Entregado
            </h3>
            <div className="space-y-2">
              {redemptionRequests.filter((r: any) => r.status === 'completed').map((r: any) => (
                <div key={r.id} className="p-3 bg-white rounded-lg border border-green-100 shadow-sm">
                  <p className="font-medium text-gray-900 text-sm">{r.client_name}</p>
                  <p className="text-xs text-gray-600">{r.redemption_name} · {r.points_spent} pts</p>
                  <p className="text-xs text-gray-500">{new Date(r.created_at).toLocaleDateString('es-AR')}</p>
                </div>
              ))}
              {redemptionRequests.filter((r: any) => r.status === 'completed').length === 0 && (
                <p className="text-sm text-gray-500">Ninguno</p>
              )}
            </div>
          </div>
        </div>
      </Card>

      {/* Ajuste de Puntos */}
      <Card className="p-6">
        <div className="flex items-center space-x-3 mb-4">
          <Star className="w-6 h-6 text-yellow-600" />
          <h2 className="text-xl font-semibold">Ajuste de Puntos</h2>
        </div>
        <p className="text-sm text-gray-600 mb-4">Suma o resta puntos manualmente a un cliente. Positivo = sumar, Negativo = restar.</p>
        <div className="flex flex-wrap gap-4 items-end">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Cliente</label>
            <select
              value={adjustClientId || ''}
              onChange={(e) => setAdjustClientId(e.target.value || null)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Seleccionar cliente...</option>
              {clubClients.map((c: any) => (
                <option key={c.client_id} value={c.client_id}>{c.client_name} ({c.points_balance ?? 0} pts)</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Puntos (+ o -)</label>
            <Input type="number" value={adjustAmount} onChange={(e) => setAdjustAmount(e.target.value)} placeholder="Ej: 100 o -50" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Motivo (opcional)</label>
            <Input value={adjustReason} onChange={(e) => setAdjustReason(e.target.value)} placeholder="Ajuste por..." />
          </div>
          <Button onClick={handleAdjustPoints} disabled={saving || !adjustClientId || !adjustAmount}>
            {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Aplicar
          </Button>
        </div>
      </Card>

      {/* Clientes Club */}
      <Card className="p-6">
        <div className="flex items-center space-x-3 mb-4">
          <Users className="w-6 h-6 text-indigo-600" />
          <h2 className="text-xl font-semibold">Clientes - Club Productex</h2>
        </div>
        <p className="text-sm text-gray-600 mb-4">Ver clientes con puntos, crear cuenta de portal o editar contraseña.</p>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Cliente</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Puntos</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Nivel</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Portal</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Acciones</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {clubClients.map((c: any) => (
                <tr key={c.client_id}>
                  <td className="px-4 py-2 text-sm">{c.client_name}</td>
                  <td className="px-4 py-2 text-sm font-medium">{c.points_balance ?? 0}</td>
                  <td className="px-4 py-2 text-sm">{c.current_level || '-'}</td>
                  <td className="px-4 py-2 text-sm">{c.has_portal ? <span className="text-green-600">Sí</span> : <span className="text-gray-400">No</span>}</td>
                  <td className="px-4 py-2 text-right space-x-2">
                    {!c.has_portal ? (
                      <Button size="sm" variant="outline" icon={<UserPlus className="w-4 h-4" />} onClick={() => openPortalModal(c)}>Crear Portal</Button>
                    ) : (
                      <Button size="sm" variant="outline" icon={<Lock className="w-4 h-4" />} onClick={() => openPasswordModal(c)}>Editar Contraseña</Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {clubClients.length === 0 && <p className="text-center text-gray-500 py-4">No hay clientes</p>}
      </Card>

      {/* Modales Portal y Contraseña */}
      {showPortalModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full">
            <div className="flex justify-between mb-4">
              <h3 className="text-lg font-semibold">Crear cuenta de portal</h3>
              <button onClick={() => setShowPortalModal(false)} className="text-gray-500 hover:text-gray-700"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-sm text-gray-600 mb-2">Cliente: {portalNombre}</p>
            <div className="space-y-3 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                <Input value={portalEmail} onChange={(e) => setPortalEmail(e.target.value)} placeholder="cliente@email.com" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
                <Input value={portalNombre} onChange={(e) => setPortalNombre(e.target.value)} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Empresa</label>
                <Input value={portalEmpresa} onChange={(e) => setPortalEmpresa(e.target.value)} />
              </div>
            </div>
            {tempPassword && (
              <div className="p-3 bg-green-50 rounded mb-4">
                <p className="text-sm font-medium text-green-800">Contraseña temporal:</p>
                <code className="block mt-2 p-2 bg-white rounded text-sm">{tempPassword}</code>
                <button
                  className="mt-2 flex items-center gap-2 text-sm text-green-700 hover:underline"
                  onClick={() => { navigator.clipboard.writeText(tempPassword); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
                >
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  {copied ? 'Copiado' : 'Copiar'}
                </button>
              </div>
            )}
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowPortalModal(false)}>Cancelar</Button>
              <Button onClick={handleCreatePortal} disabled={saving}>{saving ? 'Creando...' : 'Crear cuenta'}</Button>
            </div>
          </div>
        </div>
      )}
      {showPasswordModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full">
            <div className="flex justify-between mb-4">
              <h3 className="text-lg font-semibold">Cambiar contraseña</h3>
              <button onClick={() => setShowPasswordModal(false)} className="text-gray-500 hover:text-gray-700"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-sm text-gray-600 mb-4">Cliente: {passwordClientName}</p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Nueva contraseña (mín. 6 caracteres)</label>
              <Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="********" />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowPasswordModal(false)}>Cancelar</Button>
              <Button onClick={handleChangePassword} disabled={saving || newPassword.length < 6}>{saving ? 'Guardando...' : 'Guardar'}</Button>
            </div>
          </div>
        </div>
      )}

      {/* System Information */}
      <Card className="p-6">
        <div className="flex items-center space-x-3 mb-4">
          <Info className="w-6 h-6 text-blue-600" />
          <h2 className="text-xl font-semibold">Información del Sistema</h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <div className="text-2xl font-bold text-blue-600">{levels.length}</div>
            <div className="text-sm text-gray-600">Niveles Configurados</div>
          </div>
          
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <div className="text-2xl font-bold text-green-600">
              {levels.filter(l => l.is_active).length}
            </div>
            <div className="text-sm text-gray-600">Niveles Activos</div>
          </div>
          
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <div className="text-2xl font-bold text-purple-600">
              {config?.points_per_1000_pesos || 1}
            </div>
            <div className="text-sm text-gray-600">Puntos por $1,000</div>
          </div>
          
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <div className="text-2xl font-bold text-red-600">
              {redemptions.filter(r => r.is_active).length}
            </div>
            <div className="text-sm text-gray-600">Productos Activos</div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default ClubProductexConfig;
